import ThumbImg from "../media/ThumbImg";
import { readableNameColor } from "../../lib/nameColor";
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { toast } from '../../lib/ui';
import CloseButton from '../common/CloseButton';
import { useDialogA11y } from '../../hooks/useDialogA11y';

type Friend = { id: string; username?: string; name?: string; avatarUrl?: string | null; status?: string; nameColor?: string | null; lastSeen?: string | null };
type IncomingReq = { id: string; fromUserId: string; fromUsername?: string; fromName?: string; fromAvatarUrl?: string | null; fromStatus?: string; createdAt?: string };
type OutgoingReq = { id: string; toUserId: string; toUsername?: string; toName?: string; toAvatarUrl?: string | null; toStatus?: string; createdAt?: string };
type DiscoveredUser = { id: string; username?: string; name?: string; avatarUrl?: string | null; nameColor?: string | null; outgoing?: boolean; incoming?: boolean };

type TabId = 'friends' | 'requests';

function formatLastSeen(ts?: string | null): string {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleString();
  } catch {
    return '';
  }
}

function presenceOf(f: Friend, onlineIds?: string[]) {
  const isOnline = !!(onlineIds && onlineIds.includes(f.id));
  const st = String(f.status || '').toLowerCase();
  if (!isOnline) return { isOnline: false, label: 'Offline', dot: 'bg-neutral-500' };
  if (st === 'dnd') return { isOnline: true, label: 'Do Not Disturb', dot: 'bg-red-500' };
  if (st === 'idle') return { isOnline: true, label: 'Idle', dot: 'bg-amber-500' };
  return { isOnline: true, label: 'Online', dot: 'bg-online' };
}

function Avatar({ f, dot }: { f: { name?: string; username?: string; avatarUrl?: string | null }; dot?: string }) {
  const initial = (f.name?.[0] || f.username?.[0] || '?').toUpperCase();
  return (
    <div className="relative shrink-0">
      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-white/10 ring-1 ring-white/10">
        {f.avatarUrl ? <ThumbImg w={56} src={f.avatarUrl} alt={f.name || f.username || ''} className="h-full w-full object-cover" /> : <span className="text-sm text-fg-2">{initial}</span>}
      </div>
      {dot && <span className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full ring-2 ring-black/40 ${dot}`} />}
    </div>
  );
}

function EmptyState({ icon, title, subtitle, action }: { icon: React.ReactNode; title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <div className="surface-inset flex h-14 w-14 items-center justify-center rounded-2xl text-fg-3">{icon}</div>
      <div className="text-sm font-medium text-fg-2">{title}</div>
      {subtitle && <div className="max-w-xs text-xs leading-relaxed text-fg-3">{subtitle}</div>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="px-2 pb-1 pt-3 text-xs font-medium text-fg-3">{children}</div>;
}

export default function FriendsModal({
  token,
  open,
  onClose,
  onStartDm,
  onStartCall,
  onlineIds,
}: {
  token: string;
  open: boolean;
  onClose: () => void;
  onStartDm: (userId: string) => void | Promise<void>;
  onStartCall: (userId: string, displayName?: string) => void | Promise<void>;
  onlineIds?: string[];
}) {
  const [tab, setTab] = useState<TabId>('friends');
  const [friends, setFriends] = useState<Friend[]>([]);
  const [incoming, setIncoming] = useState<IncomingReq[]>([]);
  const [outgoing, setOutgoing] = useState<OutgoingReq[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [query, setQuery] = useState('');
  const [menuForId, setMenuForId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [discovered, setDiscovered] = useState<DiscoveredUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchBlocked, setSearchBlocked] = useState(false);
  const [requestedIds, setRequestedIds] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);

  async function load() {
    setLoading(true); setErr('');
    try {
      const f = await api.getAuth('/friends/list', token);
      setFriends(f.friends || []);
      const r = await api.getAuth('/friends/requests', token);
      setIncoming(r.incoming || []);
      setOutgoing(r.outgoing || []);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      load();
      setTab('friends'); setQuery(''); setMenuForId(null);
      setDiscovered([]); setRequestedIds(new Set()); setSearchBlocked(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reloads when the modal opens; uses the current render's load()
  }, [open]);

  // Debounced user discovery: searching the friends bar also finds people you're
  // not friends with (server filters out yourself, existing friends, blocked users
  // and anyone who opted out of search).
  useEffect(() => {
    const q = query.trim();
    if (!open || tab !== 'friends' || q.length < 2) { setDiscovered([]); setSearching(false); setSearchBlocked(false); return; }
    setSearching(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await api.getAuth(`/friends/search?q=${encodeURIComponent(q)}`, token);
        setDiscovered(Array.isArray(res?.users) ? res.users : []);
        setSearchBlocked(false);
      } catch (e: any) {
        // Rate limited: keep the last results and stay quiet rather than flashing
        // an error or a misleading "no people found".
        if (e?.status === 429) setSearchBlocked(true);
        else { setDiscovered([]); setSearchBlocked(false); }
      } finally { setSearching(false); }
    }, 300);
    return () => window.clearTimeout(t);
  }, [query, tab, open, token]);

  async function respond(id: string, action: 'accept' | 'decline') {
    setLoading(true); setErr('');
    try { await api.postAuth('/friends/respond', { requestId: id, action }, token); await load(); }
    catch (e: any) { setErr(e?.message || 'Failed to respond'); }
    finally { setLoading(false); }
  }

  async function removeFriend(uid: string) {
    setMenuForId(null);
    setLoading(true); setErr('');
    try { await api.deleteAuth('/friends', { userId: uid }, token); await load(); toast('Friend removed', 'success'); }
    catch (e: any) { setErr(e?.message || 'Failed to remove'); }
    finally { setLoading(false); }
  }

  async function blockFriend(uid: string, name: string) {
    setMenuForId(null);
    if (!window.confirm(`Block ${name}? They'll be removed as a friend and can't message you.`)) return;
    setLoading(true); setErr('');
    try { await api.postAuth('/users/block', { userId: uid }, token); await load(); toast('User blocked', 'success'); }
    catch (e: any) { setErr(e?.message || 'Failed to block'); }
    finally { setLoading(false); }
  }

  async function sendRequest(u: DiscoveredUser) {
    setErr('');
    // Optimistically mark as requested so the button updates immediately.
    setRequestedIds((prev) => new Set(prev).add(u.id));
    try {
      const res = await api.postAuth('/friends/request', { toUserId: u.id }, token);
      if (res?.autoAccepted) { toast('Friend added', 'success'); await load(); }
      else if (res?.already) { toast('Already friends', 'info'); await load(); }
      else { toast('Request sent', 'success'); }
    } catch (e: any) {
      setRequestedIds((prev) => { const n = new Set(prev); n.delete(u.id); return n; });
      setErr(e?.message || 'Failed to send request');
    }
  }

  const visibleFriends = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = friends.filter((f) => {
      if (!q) return true;
      return (f.name || '').toLowerCase().includes(q) || (f.username || '').toLowerCase().includes(q);
    });
    return [...list].sort((a, b) => {
      const ao = presenceOf(a, onlineIds).isOnline ? 0 : 1;
      const bo = presenceOf(b, onlineIds).isOnline ? 0 : 1;
      if (ao !== bo) return ao - bo;
      return (a.name || a.username || '').localeCompare(b.name || b.username || '');
    });
  }, [friends, query, onlineIds]);

  const querying = query.trim().length >= 2;

  const tabs: { id: TabId; label: string; badge?: number }[] = [
    { id: 'friends', label: 'Friends' },
    { id: 'requests', label: 'Requests', badge: incoming.length },
  ];
  const tabIndex = Math.max(0, tabs.findIndex((t) => t.id === tab));

  // Subtle staggered entrance for the default friends list (skipped while searching
  // so filtering doesn't replay the cascade on every keystroke).
  const rowStagger = (i: number): React.CSSProperties | undefined =>
    querying ? undefined : {
      animationName: 'fadeInUp',
      animationDuration: '240ms',
      animationTimingFunction: 'var(--ease-out-strong)',
      animationFillMode: 'backwards',
      animationDelay: `${Math.min(i, 12) * 25}ms`,
    };

  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(open, dialogRef, onClose);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[6500] flex items-center justify-center p-3">
      <div className="overlay-fade absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="friends-modal-title" tabIndex={-1} className="overlay-pop surface-glass-raised relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl outline-none">
        <div className="flex items-start justify-between px-5 pt-5">
          <div>
            <h2 id="friends-modal-title" className="text-xl font-medium text-white">Friends</h2>
            <p className="text-xs text-fg-3">{friends.length} {friends.length === 1 ? 'friend' : 'friends'}</p>
          </div>
          <CloseButton onClick={onClose} />
        </div>

        {/* Segmented tabs with a sliding indicator */}
        <div className="px-5 pt-4">
          <div className="surface-inset relative grid grid-cols-2 rounded-2xl p-1 text-sm">
            <span
              aria-hidden
              className="absolute top-1 bottom-1 rounded-xl bg-white/10 transition-transform duration-200 ease-out"
              style={{ left: '0.25rem', width: 'calc((100% - 0.5rem) / 2)', transform: `translateX(${tabIndex * 100}%)` }}
            />
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`relative z-10 flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 font-medium transition-colors active:scale-[0.98] ${tab === t.id ? 'text-brand' : 'text-fg-2 hover:text-fg-2'}`}
              >
                {t.label}
                {!!t.badge && t.badge > 0 && (
                  <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-meta font-semibold text-emerald-950">
                    {t.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="px-5 pt-3 text-sm text-red-400">{err}</div>}

        <div className="min-h-0 flex-1 overflow-auto px-3 pb-4 pt-2 sm:px-4">
          {tab === 'friends' && (
            <>
              <div className="relative mb-2 px-1">
                <svg viewBox="0 0 24 24" aria-hidden className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search friends or find people"
                  aria-label="Search friends or find people"
                  enterKeyHint="search"
                  className="surface-inset w-full rounded-2xl py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-fg-3 focus:outline-none focus:ring-1 focus:ring-emerald-400/40"
                />
              </div>

              {/* Existing friends (filtered while typing) */}
              {querying && visibleFriends.length > 0 && <SectionLabel>Friends</SectionLabel>}
              <div className="space-y-0.5">
                {visibleFriends.map((f, i) => {
                  const p = presenceOf(f, onlineIds);
                  const subjectAllowsLast = (f as any)?.shareLastOnline !== false;
                  const ago = subjectAllowsLast && !p.isOnline ? formatLastSeen(f.lastSeen) : '';
                  return (
                    <div key={f.id} style={rowStagger(i)} className="group flex items-center justify-between gap-2 rounded-2xl px-2 py-2 transition-colors hover:bg-white/[0.06]">
                      <div className="flex min-w-0 items-center gap-3">
                        <Avatar f={f} dot={p.dot} />
                        <div className="min-w-0">
                          <div className="truncate text-sm text-white" style={readableNameColor(f.nameColor) ? { color: readableNameColor(f.nameColor) } : undefined}>{f.name || f.username}</div>
                          <div className="truncate text-xs text-fg-3">{p.label}{ago ? ` · ${ago}` : ''}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button
                          className="flex h-9 w-9 items-center justify-center rounded-xl text-fg-2 transition hover:bg-white/10 hover:text-white active:scale-95"
                          onClick={() => onStartDm(f.id)}
                          title="Message"
                          aria-label="Message friend"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                        </button>
                        <button
                          className="flex h-9 w-9 items-center justify-center rounded-xl text-fg-2 transition hover:bg-white/10 hover:text-white active:scale-95"
                          onClick={() => onStartCall(f.id, f.name || f.username || 'Direct contact')}
                          title="Call"
                          aria-label="Call friend"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 5.15 12.8 19.79 19.79 0 0 1 2.08 4.16 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.89.33 1.77.62 2.61a2 2 0 0 1-.45 2.11L8 9.66a16 16 0 0 0 6.34 6.34l1.22-1.23a2 2 0 0 1 2.11-.45c.84.29 1.72.5 2.61.62A2 2 0 0 1 22 16.92z" /></svg>
                        </button>
                        <div className="relative">
                          <button
                            className="flex h-9 w-9 items-center justify-center rounded-xl text-fg-2 transition hover:bg-white/10 hover:text-white active:scale-95"
                            onClick={(e) => {
                              if (menuForId === f.id) { setMenuForId(null); return; }
                              const r = e.currentTarget.getBoundingClientRect();
                              const MENU_H = 92;
                              const openUp = r.bottom + MENU_H + 8 > window.innerHeight;
                              setMenuPos({
                                right: Math.max(8, window.innerWidth - r.right),
                                top: openUp ? Math.max(8, r.top - MENU_H - 4) : r.bottom + 4,
                              });
                              setMenuForId(f.id);
                            }}
                            title="More"
                            aria-label="More options"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
                          </button>
                          {menuForId === f.id && menuPos && (
                            <>
                              <div className="fixed inset-0 z-[6590]" onClick={() => setMenuForId(null)} />
                              {/* Fixed positioning escapes the modal's overflow clipping, so the menu
                                  stays reachable even with one friend (short modal) — and flips up when
                                  there's no room below. */}
                              <div className="fixed z-[6600] w-44 overflow-hidden rounded-2xl border border-white/10 bg-neutral-950/95 p-1 shadow-2xl backdrop-blur-xl" style={{ top: menuPos.top, right: menuPos.right }}>
                                <button className="w-full rounded-xl px-3 py-2 text-left text-sm text-fg-2 transition hover:bg-white/5" onClick={() => removeFriend(f.id)}>Remove friend</button>
                                <button className="w-full rounded-xl px-3 py-2 text-left text-sm text-red-300 transition hover:bg-red-900/25" onClick={() => blockFriend(f.id, f.name || f.username || 'this user')}>Block</button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* People you can add (discovery) */}
              {querying && (
                <>
                  <SectionLabel>People</SectionLabel>
                  {searching && discovered.length === 0 && <div className="px-2 py-3 text-sm text-fg-3">Searching…</div>}
                  <div className="space-y-0.5">
                    {discovered.map((u) => {
                      const requested = !!u.outgoing || requestedIds.has(u.id);
                      return (
                        <div key={u.id} className="flex items-center justify-between gap-2 rounded-2xl px-2 py-2 transition-colors hover:bg-white/[0.06]">
                          <div className="flex min-w-0 items-center gap-3">
                            <Avatar f={u} />
                            <div className="min-w-0">
                              <div className="truncate text-sm text-white" style={readableNameColor(u.nameColor) ? { color: readableNameColor(u.nameColor) } : undefined}>{u.name || u.username}</div>
                              {u.username && <div className="truncate text-xs text-fg-3">@{u.username}</div>}
                            </div>
                          </div>
                          {requested ? (
                            <span className="inline-flex items-center gap-1 rounded-xl bg-white/5 px-2.5 py-1.5 text-xs text-fg-3">
                              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                              Requested
                            </span>
                          ) : (
                            <button
                              className="inline-flex items-center gap-1 rounded-xl bg-emerald-500/15 px-2.5 py-1.5 text-xs font-medium text-emerald-200 transition hover:bg-emerald-500/25 active:scale-95"
                              onClick={() => sendRequest(u)}
                            >
                              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                              Add
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {!searching && !searchBlocked && discovered.length === 0 && (
                      <div className="px-2 py-3 text-sm text-fg-3">No people found for “{query.trim()}”.</div>
                    )}
                  </div>
                </>
              )}

              {/* Empty state when not searching and no friends yet */}
              {!querying && friends.length === 0 && !loading && (
                <EmptyState
                  icon={<svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>}
                  title="No friends yet"
                  subtitle="Search by name or username to find people and send a friend request."
                  action={(
                    <button
                      className="rounded-xl bg-emerald-500/15 px-3 py-1.5 text-sm text-emerald-200 transition hover:bg-emerald-500/25 active:scale-[0.98]"
                      onClick={() => searchRef.current?.focus()}
                    >
                      Find people
                    </button>
                  )}
                />
              )}
            </>
          )}

          {tab === 'requests' && (
            <div className="grid grid-cols-1 gap-3 px-1 sm:grid-cols-2">
              <div className="surface-inset overflow-hidden rounded-2xl">
                <div className="px-3 py-2 text-xs font-medium text-fg-3">Incoming</div>
                <div className="max-h-[46vh] overflow-auto p-1">
                  {incoming.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 rounded-xl p-2 hover:bg-white/[0.05]">
                      <div className="flex min-w-0 items-center gap-3">
                        <Avatar f={{ name: r.fromName, username: r.fromUsername, avatarUrl: r.fromAvatarUrl }} />
                        <div className="min-w-0">
                          <div className="truncate text-sm text-fg">{r.fromName || r.fromUsername || r.fromUserId}</div>
                          {r.fromUsername && <div className="truncate text-xs text-fg-3">@{r.fromUsername}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button className="rounded-lg bg-emerald-500/15 px-2.5 py-1 text-sm text-emerald-200 transition hover:bg-emerald-500/25 active:scale-95" onClick={() => respond(r.id, 'accept')}>Accept</button>
                        <button className="rounded-lg bg-white/5 px-2.5 py-1 text-sm text-fg-2 transition hover:bg-white/10 active:scale-95" onClick={() => respond(r.id, 'decline')}>Decline</button>
                      </div>
                    </div>
                  ))}
                  {incoming.length === 0 && <div className="px-3 py-6 text-center text-xs text-fg-3">No incoming requests.</div>}
                </div>
              </div>
              <div className="surface-inset overflow-hidden rounded-2xl">
                <div className="px-3 py-2 text-xs font-medium text-fg-3">Outgoing</div>
                <div className="max-h-[46vh] overflow-auto p-1">
                  {outgoing.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 rounded-xl p-2">
                      <Avatar f={{ name: r.toName, username: r.toUsername, avatarUrl: r.toAvatarUrl }} />
                      <div className="min-w-0">
                        <div className="truncate text-sm text-fg">{r.toName || r.toUsername || r.toUserId}</div>
                        <div className="text-xs text-amber-300/70">Pending</div>
                      </div>
                    </div>
                  ))}
                  {outgoing.length === 0 && <div className="px-3 py-6 text-center text-xs text-fg-3">No outgoing requests.</div>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
